import React, { useState } from 'react';
import axios from 'axios';
import API_URL from './config';

function FileUpload() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('Reliance TV');

  // Fetch brands on component mount
  React.useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/brands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrands(response.data);
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError('');
    setResult(null);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('brand', selectedBrand);

    try {
      const response = await axios.post(`${API_URL}/api/upload-allocate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);

      if (response.data.downloadUrl) {
        window.open(`${API_URL}${response.data.downloadUrl}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fade-in">
      <h1 className="page-title">Upload Calls File</h1>

      <div className="premium-card" style={{ maxWidth: '800px', margin: '0 auto' }}>

        {/* Brand Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Select Brand
          </label>
          <select
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
            className="premium-select"
            style={{ width: '100%', padding: '12px' }}
          >
            {brands.map(brand => (
              <option key={brand.id} value={brand.name}>
                {brand.name} {brand.sla_days ? `(SLA: ${brand.sla_days} days)` : '(No SLA)'}
              </option>
            ))}
          </select>
        </div>

        <div
          className={`upload-area ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="upload-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>

          <h3>Drag & drop your file here</h3>
          <p>or</p>

          <input
            type="file"
            id="fileInput"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button
            className="premium-button"
            onClick={() => document.getElementById('fileInput').click()}
          >
            Browse Files
          </button>

          {file && (
            <div className="file-info">
              <span className="file-name">{file.name}</span>
              <span className="file-size">{(file.size / 1024).toFixed(2)} KB</span>
            </div>
          )}
        </div>

        {file && (
          <button
            onClick={handleUpload}
            disabled={loading}
            className="premium-button success"
            style={{ width: '100%', marginTop: '20px' }}
          >
            {loading ? (
              <span className="loading-spinner" style={{ width: 20, height: 20 }} />
            ) : (
              'Process & Allocate Calls'
            )}
          </button>
        )}

        {error && (
          <div className="message-toast error" style={{ position: 'static', marginTop: '20px' }}>
            {error}
          </div>
        )}

        {result && (
          <div className="premium-card" style={{ marginTop: '24px', background: 'var(--system-green)10' }}>
            <h3 style={{ color: 'var(--system-green)' }}>Allocation Complete!</h3>

            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 0 }}>
              <div className="stat-item">
                <span className="stat-label">Total Calls</span>
                <span className="stat-value">{result.summary.total}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Allocated</span>
                <span className="stat-value" style={{ color: 'var(--system-green)' }}>{result.summary.allocated}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Unallocated</span>
                <span className="stat-value" style={{ color: 'var(--system-orange)' }}>{result.summary.unallocated}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Emails Sent</span>
                <span className="stat-value" style={{ color: 'var(--system-blue)' }}>{result.summary.emails_sent || 0}</span>
              </div>
            </div>

            <div className="steps-breakdown">
              <h4>Allocation Steps</h4>
              <div className="step-item">
                <span>Step 1 (Same PIN)</span>
                <span className="step-count">{result.summary.step1}</span>
              </div>
              <div className="step-item">
                <span>Step 2 (Nearby PIN)</span>
                <span className="step-count">{result.summary.step2}</span>
              </div>
              <div className="step-item">
                <span>Step 3 (Same City)</span>
                <span className="step-count">{result.summary.step3}</span>
              </div>
              <div className="step-item">
                <span>Step 4 (Same State)</span>
                <span className="step-count">{result.summary.step4}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FileUpload;